/**
 * mentionUtils.ts — Consolidated mention utilities for canvas-v2
 *
 * Provides shared helpers for building mention candidate lists from
 * canvas nodes. Used by RichTextBox (@ mention dropdown) and
 * ReferenceSelector (asset grid).
 *
 * Replaces duplicated buildCandidates / getNodeLabel / getNodeIcon
 * from ChatInput, InlineMentionComposer, and DirectorNode/MentionInput.
 */

import type { Node as ReactFlowNode } from 'reactflow'
import type {
  CharacterAsset,
  GeneratedAudioWithHistory,
  GeneratedImageWithHistory,
  GeneratedVideoWithHistory,
  LocationAsset,
  ObjectAsset,
} from '../../../../generated/nodeDataTypes'
import { nodeSchemas } from '../../nodeRegistry'

// ─── Types ───────────────────────────────────────────────────────────────────

export interface MentionCandidate {
  id: string
  type: string
  label: string
  icon: string
  thumbnailPath?: string
}

export type MediaType = 'image' | 'video' | 'audio'

export interface CanvasAssetCandidate {
  nodeId: string
  nodeType: string
  label: string
  icon: string
  assetPath: string
  mediaType: MediaType
}

// ─── Node Image Path Extractors ──────────────────────────────────────────────

const IMAGE_EXTRACTORS: Record<
  string,
  {
    typeLabel: string
    getImagePath: (data: Record<string, unknown>) => string | undefined
  }
> = {
  characterDesigner: {
    typeLabel: 'Character',
    getImagePath: (d) => {
      const sheet = d.character_sheet as string | undefined
      if (sheet && !sheet.endsWith('.json')) return sheet
      const asset = d.out_character_sheet as CharacterAsset | undefined
      const main = asset?.images?.main
      if (main?.history?.length) {
        return main.history[main.picked_index ?? 0]?.result?.local_path
      }
      return undefined
    },
  },
  locationDesigner: {
    typeLabel: 'Location',
    getImagePath: (d) => {
      const img = d.location_image as string | undefined
      if (img) return img
      const asset = d.out_location_image as LocationAsset | undefined
      const main = asset?.images?.main
      if (main?.history?.length) {
        return main.history[main.picked_index ?? 0]?.result?.local_path
      }
      return undefined
    },
  },
  objectDesigner: {
    typeLabel: 'Object',
    getImagePath: (d) => {
      const img = d.object_image as string | undefined
      if (img) return img
      const asset = d.out_object_image as ObjectAsset | undefined
      const main = asset?.images?.main
      if (main?.history?.length) {
        return main.history[main.picked_index ?? 0]?.result?.local_path
      }
      return undefined
    },
  },
  imageGenerator: {
    typeLabel: 'Generated',
    getImagePath: (d) => {
      const path = d.generated_image_path as string | undefined
      if (path) return path
      const gen = d.out_generated_image as GeneratedImageWithHistory | undefined
      if (gen?.history?.length) {
        return gen.history[gen.picked_index ?? 0]?.result?.local_path
      }
      return undefined
    },
  },
  image: {
    typeLabel: 'Upload',
    getImagePath: (d) => d.out_image as string | undefined,
  },
}

// ─── Node Video Path Extractors ──────────────────────────────────────────────

const VIDEO_EXTRACTORS: Record<
  string,
  {
    typeLabel: string
    getVideoPath: (data: Record<string, unknown>) => string | undefined
  }
> = {
  video: {
    typeLabel: 'Upload',
    getVideoPath: (d) => d.out_video as string | undefined,
  },
  videoGenerator: {
    typeLabel: 'Video',
    getVideoPath: (d) => {
      const path = d.generated_video_path as string | undefined
      if (path) return path
      const gen = d.out_generated_video as GeneratedVideoWithHistory | undefined
      if (gen?.history?.length) {
        return gen.history[gen.picked_index ?? 0]?.result?.local_path
      }
      return undefined
    },
  },
  videoEditor: {
    typeLabel: 'Edited',
    getVideoPath: (d) => d.out_video_output as string | undefined,
  },
}

// ─── Node Audio Path Extractors ──────────────────────────────────────────────

const AUDIO_EXTRACTORS: Record<
  string,
  {
    typeLabel: string
    getAudioPath: (data: Record<string, unknown>) => string | undefined
  }
> = {
  audio: {
    typeLabel: 'Upload',
    getAudioPath: (d) => d.out_audio as string | undefined,
  },
  speechGenerator: {
    typeLabel: 'Speech',
    getAudioPath: (d) => {
      const path = d.generated_audio_path as string | undefined
      if (path) return path
      const gen = d.out_generated_audio as GeneratedAudioWithHistory | undefined
      if (gen?.history?.length) {
        return gen.history[gen.picked_index ?? 0]?.result?.local_path
      }
      return undefined
    },
  },
  musicGenerator: {
    typeLabel: 'Music',
    getAudioPath: (d) => {
      const path = d.generated_audio_path as string | undefined
      if (path) return path
      const gen = d.out_generated_audio as GeneratedAudioWithHistory | undefined
      if (gen?.history?.length) {
        return gen.history[gen.picked_index ?? 0]?.result?.local_path
      }
      return undefined
    },
  },
}

// ─── Core Functions ──────────────────────────────────────────────────────────

/**
 * Get a display label for a canvas node.
 * Checks common name fields, falls back to schema label, then node type.
 */
export function getNodeLabel(node: ReactFlowNode): string {
  const data = node.data as Record<string, unknown> | undefined
  if (data) {
    for (const key of ['name', 'character_name', 'location_name', 'in_name', 'label']) {
      const value = data[key]
      if (typeof value === 'string' && value.trim()) {
        return value.trim()
      }
    }
    // For imageGenerator, use truncated prompt as label
    if (node.type === 'imageGenerator') {
      const prompt = data.in_prompt as string | undefined
      if (prompt) return prompt.length > 30 ? prompt.slice(0, 30) + '...' : prompt
    }
  }
  const schema = node.type ? nodeSchemas[node.type] : undefined
  if (schema) return schema.label
  return node.type ?? node.id
}

/**
 * Get the icon emoji for a node type from its schema.
 */
export function getNodeIcon(nodeType: string): string {
  const schema = nodeSchemas[nodeType]
  return (schema as { icon?: string } | undefined)?.icon ?? ''
}

/**
 * Get the thumbnail path (image) for a node, if available.
 */
export function getNodeThumbnailPath(node: ReactFlowNode): string | undefined {
  const nodeType = node.type ?? ''
  const data = node.data as Record<string, unknown>
  const extractor = IMAGE_EXTRACTORS[nodeType]
  if (extractor) return extractor.getImagePath(data)
  return undefined
}

/**
 * Build mention candidates from canvas nodes.
 * Excludes the 'director' node type.
 */
export function buildCandidates(nodes: ReactFlowNode[]): MentionCandidate[] {
  return nodes
    .filter((node) => node.type !== 'director')
    .map((node) => ({
      id: node.id,
      type: node.type ?? '',
      label: getNodeLabel(node),
      icon: getNodeIcon(node.type ?? ''),
      thumbnailPath: getNodeThumbnailPath(node),
    }))
}

/**
 * Build canvas asset candidates for the reference selector.
 * Groups by media type (image/video/audio).
 * Excludes the node that opened the selector (excludeNodeId).
 */
export function buildAssetCandidates(
  nodes: ReactFlowNode[],
  excludeNodeId?: string
): CanvasAssetCandidate[] {
  const results: CanvasAssetCandidate[] = []

  for (const node of nodes) {
    if (node.id === excludeNodeId) continue
    const nodeType = node.type ?? ''
    const data = node.data as Record<string, unknown>

    // Check image extractors
    const imageExtractor = IMAGE_EXTRACTORS[nodeType]
    if (imageExtractor) {
      const path = imageExtractor.getImagePath(data)
      if (path) {
        results.push({
          nodeId: node.id,
          nodeType,
          label: getNodeLabel(node),
          icon: getNodeIcon(nodeType),
          assetPath: path,
          mediaType: 'image',
        })
      }
    }

    // Check video extractors
    const videoExtractor = VIDEO_EXTRACTORS[nodeType]
    if (videoExtractor) {
      const path = videoExtractor.getVideoPath(data)
      if (path) {
        results.push({
          nodeId: node.id,
          nodeType,
          label: getNodeLabel(node),
          icon: getNodeIcon(nodeType),
          assetPath: path,
          mediaType: 'video',
        })
      }
    }

    // Check audio extractors
    const audioExtractor = AUDIO_EXTRACTORS[nodeType]
    if (audioExtractor) {
      const path = audioExtractor.getAudioPath(data)
      if (path) {
        results.push({
          nodeId: node.id,
          nodeType,
          label: getNodeLabel(node),
          icon: getNodeIcon(nodeType),
          assetPath: path,
          mediaType: 'audio',
        })
      }
    }
  }

  return results
}
